'use client';

import { useCallback, useMemo, useTransition } from 'react';
import { parseAsString, useQueryState } from 'nuqs';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { Trans } from '@kit/ui/trans';

import {
  ListingFilterBar,
  ListingFilterSheet,
  useListingFilters,
} from '~/components/listing-filters';
import { dancersFilterConfig } from '~/lib/listings/dancers-filter-config';
import type { DancerListItem } from '../_lib/server/get-dancers';
import { DancerGrid } from './dancer-grid';

// The dancers listing defaults to professionals (`level=pro`) when the param is
// ABSENT (BAL-362) — modeled here as the nuqs default, so 'pro' writes a clean
// (param-less) URL and the loader's `params.level ?? 'pro'` agrees. 'all' is a
// filter-only sentinel the loader treats as no level filter.
const DANCE_LEVELS = [
  { value: 'all', label: 'All Levels' },
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'prePro', label: 'Pre-Professional' },
  { value: 'pro', label: 'Professional' },
] as const;

interface DancersContainerProps {
  dancers: DancerListItem[];
  /** Super-admin only (BAL-392) — forwarded to the grid's DancerCards. */
  showAdminEdit?: boolean;
}

export function DancersContainer({
  dancers,
  showAdminEdit = false,
}: DancersContainerProps) {
  const { t } = useTranslation('dancers');

  const translate = useCallback(
    (key: string, defaultValue?: string) =>
      defaultValue ? t(key, { defaultValue }) : t(key),
    [t],
  );

  // gender + search go through the shared bar; level stays a surface slot.
  const config = useMemo(() => dancersFilterConfig(translate), [translate]);
  const filters = useListingFilters(config);

  const [levelPending, startLevelTransition] = useTransition();
  const [level, setLevel] = useQueryState(
    'level',
    parseAsString.withDefault('pro').withOptions({
      shallow: false,
      scroll: false,
      history: 'push',
      startTransition: startLevelTransition,
    }),
  );

  const isLoading = filters.isPending || levelPending;

  // Dance-level toggle (leading slot) — surface-managed to preserve absent=pro.
  const levelToggle = (
    <div
      className="flex flex-wrap gap-1"
      role="group"
      aria-label={t('filters.level', { defaultValue: 'Level' })}
    >
      {DANCE_LEVELS.map((lvl) => (
        <Button
          key={lvl.value}
          type="button"
          size="sm"
          variant={level === lvl.value ? 'default' : 'outline'}
          aria-pressed={level === lvl.value}
          onClick={() => void setLevel(lvl.value)}
          disabled={isLoading}
        >
          <Trans i18nKey={`dancers:levels.${lvl.value}`} defaults={lvl.label} />
        </Button>
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="hidden px-4 sm:block">
        <ListingFilterBar
          config={config}
          filters={filters}
          leading={levelToggle}
        />
      </div>
      <div className="px-4 sm:hidden">
        <ListingFilterSheet
          config={config}
          filters={filters}
          leading={levelToggle}
        />
      </div>

      <DancerGrid
        dancers={dancers}
        isLoading={isLoading}
        showAdminEdit={showAdminEdit}
      />
    </div>
  );
}
